'use client';

import { useState, type FC, useRef, useMemo, forwardRef } from 'react';

import type { PromotionWideBannerItemType, PromotionWideBannerWidgetData } from '@weberus/shared-types';
import 'swiper/css/autoplay';
import { PC_BREAKPOINT } from '@weberus/utils';
import { FloatingButton, PageIndicator, cn } from '@yanolja-rnd/nol-design-system';
import { IcArrowbackFill, IcArrowforwardFill } from '@yanolja-rnd/nol-icons';
import { chunk } from 'lodash-es';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { SwiperClass } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

export interface PromotionWideBannerProps {
  /** 프로모션배너_가로형 데이터 */
  data: PromotionWideBannerWidgetData;
  /** PC 너비 여부 */
  isPcWidth: boolean;
  /** 아이템 렌더링 함수 */
  renderItem: (item: PromotionWideBannerItemType, index: number) => React.ReactNode;
  /** 전체보기 클릭 이벤트 */
  onClickAll?: () => void;
}

/**
 * Swiper가 표시되기 위한 최소 슬라이드 개수 설정
 * PC와 모바일 환경에서 각각 다른 값을 사용
 */
const MIN_SLIDES_FOR_SWIPER = { PC: 3, MOBILE: 2 };

// 반복되는 CSS 클래스 상수화
const ITEM_WRAPPER_CLASS = 'flex items-center w-full pc:w-[632px] pc:max-w-[632px]';
const SLIDE_WRAPPER_CLASS = 'max-w-[calc(100%-24px)] items-center pc:max-w-none';
const GROUP_CONTAINER_CLASS = 'flex w-full px-4 pc:px-0 pc:mx-0 pc:gap-16';

/**
 * 배너 아이템 컴포넌트
 * 반복되는 배너 아이템 렌더링 로직을 추출하여 코드 중복을 제거
 *
 * @param item - 렌더링할 배너 아이템 데이터
 * @param index - 배너 아이템의 인덱스
 * @param renderItem - 부모 컴포넌트에서 전달받은 아이템 렌더링 함수
 */
const BannerItem: FC<{
  item: PromotionWideBannerItemType;
  index: number;
  renderItem: (item: PromotionWideBannerItemType, index: number) => React.ReactNode;
}> = ({ item, index, renderItem }) => {
  return <div className={ITEM_WRAPPER_CLASS}>{renderItem(item, index)}</div>;
};

/**
 * 페이지 인디케이터 컴포넌트
 * PC와 모바일용 인디케이터 UI 로직을 통합하여 코드 중복을 제거
 *
 * @param variant - 인디케이터 타입 ('mobile' 또는 'pc')
 * @param currentPage - 현재 페이지 번호
 * @param isPlaying - 자동 재생 상태
 * @param totalPages - 전체 페이지 수
 * @param isVisible - 인디케이터 표시 여부
 * @param onClickAll - 전체보기 클릭 이벤트 핸들러
 * @param onPlayChange - 재생/일시정지 토글 이벤트 핸들러
 */
const BannerPageIndicator: FC<{
  variant: 'mobile' | 'pc';
  currentPage: number;
  isPlaying: boolean;
  totalPages: number;
  isVisible: boolean;
  onClickAll: (() => void) | undefined;
  onPlayChange: () => void;
}> = ({ variant, currentPage, isPlaying, totalPages, isVisible, onClickAll, onPlayChange }) => {
  const isMobile = variant === 'mobile';

  return (
    <div
      className={cn(
        'mt-2 flex justify-center',
        // 모바일/PC 표시 설정
        isMobile ? 'pc:mt-16 pc:hidden' : 'hidden pc:mt-16 pc:flex',
        // 표시 여부에 따라 해당 디바이스에서 숨김 처리
        { [isMobile ? 'hidden' : 'pc:hidden']: !isVisible },
      )}
    >
      <PageIndicator
        currentPage={currentPage}
        isPlaying={isPlaying}
        onClickAll={() => onClickAll?.()}
        onPlayChange={onPlayChange}
        totalPages={totalPages}
      />
    </div>
  );
};

/**
 * Fallback 컴포넌트 - Swiper가 로드되기 전에 표시될 UI
 * SSR 및 초기 렌더링 시 사용되며, Swiper 초기화 전까지 유지됨
 *
 * @param items - 표시할 배너 아이템 배열
 * @param renderItem - 각 아이템을 렌더링하는 함수
 */
const PromotionWideBannerFallback: FC<{
  items: PromotionWideBannerItemType[];
  renderItem: (item: PromotionWideBannerItemType, index: number) => React.ReactNode;
}> = ({ items, renderItem }) => {
  return (
    <div className="w-full overflow-hidden">
      {/* 모바일 레이아웃 - PC에서는 숨겨짐 */}
      <div className="pc:hidden">
        <div className="relative flex items-center w-full">
          {/* 이전 배너의 일부분 (좌측 미리보기) - 아이템이 2개 이상일 때만 표시 */}
          {items.length > 2 && (
            <div className="absolute left-0 overflow-hidden">
              <div className="flex items-center max-w-[calc(100%-24px)] -translate-x-[calc(100%+8px)] mx-16">
                {renderItem(items[items.length - 1], items.length - 1)}
              </div>
            </div>
          )}

          {/* 현재 배너 (중앙 배치) - 항상 첫 번째 아이템 표시 */}
          <div className="flex-1 px-4 max-w-[calc(100%-24px)] mx-auto">
            {items.length > 0 && renderItem(items[0], 0)}
          </div>

          {/* 다음 배너의 일부분 (우측 미리보기) - 아이템이 1개 이상일 때만 표시 */}
          {items.length > 1 && (
            <div className="absolute right-0 overflow-hidden">
              <div className="flex items-center max-w-[calc(100%-24px)] translate-x-[calc(100%+8px)] mx-16">
                {renderItem(items[1], 1)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PC 레이아웃 - 모바일에서는 숨겨짐 */}
      <div className="hidden pc:block">
        <div className={SLIDE_WRAPPER_CLASS}>
          {/* PC에서는 첫 두 개의 아이템을 나란히 배치 */}
          <div className={GROUP_CONTAINER_CLASS}>
            {/* 첫 번째 아이템 */}
            {!!items[0] && <BannerItem index={0} item={items[0]} renderItem={renderItem} />}

            {/* 두 번째 아이템 */}
            {!!items[1] && <BannerItem index={1} item={items[1]} renderItem={renderItem} />}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 네비게이션 버튼 컴포넌트
 * forwardRef를 사용하여 ref를 하위 DOM 요소로 전달
 */
const NavigationButton = forwardRef<
  HTMLDivElement,
  {
    type: 'prev' | 'next';
    isVisible: boolean;
  }
>(({ type, isVisible }, ref) => {
  const isPrev = type === 'prev';

  return (
    <div
      className={cn(
        'absolute top-1/2 -translate-y-1/2 z-[1] hidden pc:block',
        isPrev ? 'left-0 -translate-x-2/4' : 'right-0 translate-x-2/4',
        { 'pc:hidden': !isVisible },
      )}
      ref={ref}
    >
      <FloatingButton icon={isPrev ? <IcArrowbackFill /> : <IcArrowforwardFill />} />
    </div>
  );
});

NavigationButton.displayName = 'NavigationButton';

/**
 * 프로모션 와이드 배너 컴포넌트
 * SSR 최적화를 위해 초기 렌더링은 일반 UI로 진행하고,
 * 클라이언트 측에서 Swiper로 전환하는 방식 사용
 *
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=4-13&m=dev&focus-id=7-5122}
 */
export const PromotionWideBanner: FC<PromotionWideBannerProps> = ({ data, isPcWidth, renderItem, onClickAll }) => {
  // Swiper 내비게이션 버튼 참조
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // Swiper 인스턴스 참조
  const swiperRef = useRef<SwiperClass>();

  // 배너 아이템 및 그룹 설정
  const items = data.items[0]?.items;
  const groups = useMemo(
    () => chunk(items, isPcWidth ? 2 : 1), // PC에서는 2개씩, 모바일에서는 1개씩 그룹화 (SSR 에서는 isPcWidth 가 false 로 처리되므로 주의)
    [items, isPcWidth],
  );

  // 상태 관리
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지 (슬라이드) 번호
  const [isPlaying, setIsPlaying] = useState(true); // 자동 재생 상태
  const [isSwiperReady, setIsSwiperReady] = useState(false); // Swiper 초기화 완료 상태

  // 네비게이션 및 인디케이터 표시 여부
  const showPcNav = items.length >= MIN_SLIDES_FOR_SWIPER.PC;
  const showMobileIndicator = items.length >= MIN_SLIDES_FOR_SWIPER.MOBILE;
  const showPcIndicator = items.length >= MIN_SLIDES_FOR_SWIPER.PC;
  const pcTotalPages = Math.ceil(items.length / 2); // PC에서는 2개씩 그룹화했으므로 총 페이지 수 조정

  /**
   * Swiper 구성 옵션
   * - loop: 무한 루프 활성화
   * - autoplay: 자동 재생 설정 (롤링 인터벌은 data에서 가져옴)
   * - centeredSlides: 슬라이드를 중앙에 배치
   * - navigation: 이전/다음 버튼 설정
   */
  const swiperConfig: SwiperOptions = {
    initialSlide: 0,
    slidesPerView: 'auto',
    loop: true,
    autoplay: { delay: data.rollingInterval, disableOnInteraction: false },
    threshold: 10,
    centeredSlides: true,
    loopAddBlankSlides: true,
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    },
    breakpoints: {
      // PC 화면 크기에서의 설정
      [PC_BREAKPOINT]: {
        slidesPerView: 1,
        spaceBetween: 16,
      },
    },
  };

  /**
   * 자동 재생 토글 함수
   */
  const toggleAutoplay = () => {
    swiperRef.current?.autoplay.running ? swiperRef.current.autoplay.stop() : swiperRef.current?.autoplay.start();
  };

  return (
    <div className="mt-12 mb-6 pc:my-40 pc:w-pc-size w-full">
      <div className="relative">
        {/* Swiper가 준비되기 전까지 Fallback UI 표시 - SSR 및 초기 렌더링 시 보임 */}
        {!isSwiperReady && (
          <div className="absolute inset-0 z-0 w-full">
            <PromotionWideBannerFallback items={items} renderItem={renderItem} />
          </div>
        )}

        {/*
          Swiper 컴포넌트 - 초기에는 투명하게 처리하고 준비되면 표시됨
          클라이언트 사이드에서 초기화 완료 후 opacity-100으로 변경되어 화면에 표시
        */}
        <Swiper
          className={cn('opacity-0', { 'opacity-100': isSwiperReady })}
          {...swiperConfig}
          modules={[Autoplay, Navigation]}
          onAutoplayStart={() => {
            setIsPlaying(true);
          }}
          onAutoplayStop={() => {
            setIsPlaying(false);
          }}
          onRealIndexChange={(_swiper) => {
            setCurrentPage(_swiper.realIndex + 1);
          }}
          onSwiper={(_swiper) => {
            swiperRef.current = _swiper;
            // Swiper 초기화 완료 후 상태 변경 (약간의 지연으로 렌더링 안정화)
            setTimeout(() => {
              setIsSwiperReady(true);
            }, 0);
          }}
        >
          {/* 그룹화된 아이템을 슬라이드로 렌더링 */}
          {groups.map((group, groupIdx) => (
            <SwiperSlide className={SLIDE_WRAPPER_CLASS} key={groupIdx}>
              <div className={GROUP_CONTAINER_CLASS}>
                {/* 각 그룹 내 아이템 렌더링 */}
                {group.map((item, itemIdx) => (
                  <BannerItem index={itemIdx} item={item} key={itemIdx} renderItem={renderItem} />
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 네비게이션 버튼 */}
        <NavigationButton isVisible={showPcNav} ref={prevRef} type="prev" />
        <NavigationButton isVisible={showPcNav} ref={nextRef} type="next" />
      </div>

      {/* 페이지 인디케이터 */}
      <BannerPageIndicator
        currentPage={currentPage}
        isPlaying={isPlaying}
        isVisible={showMobileIndicator}
        onClickAll={onClickAll}
        onPlayChange={toggleAutoplay}
        totalPages={items.length}
        variant="mobile"
      />

      <BannerPageIndicator
        currentPage={currentPage}
        isPlaying={isPlaying}
        isVisible={showPcIndicator}
        onClickAll={onClickAll}
        onPlayChange={toggleAutoplay}
        totalPages={pcTotalPages}
        variant="pc"
      />
    </div>
  );
};
